import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewRef
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { App } from '@df/core/app';
import { getAbsolutePosition } from '@df/dom/fn/get-absolute-position';
import { IScrollToOptions } from '@df/dom/scroll/scroll-to.service';
import { TimeoutService } from '@df/ng/timeout.service';
import find from 'lodash-es/find';
import { FormWizardComponent, formWizardUid } from '../form-wizard.component';
import { NFormWizard } from '../form-wizard.interface';
@Component({
    selector: 'form-wizard-step',
    templateUrl: './form-wizard-step.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormWizardStepComponent implements OnInit, OnDestroy, NFormWizard.IStep {
    @Input()
    id = `form-wizard-step-${formWizardUid()}`;

    @Input()
    title?: string;

    @Input()
    order = 0;
    @Output()
    submittedChange = new EventEmitter<boolean>();
    forms: NgForm[] = [];
    constructor(
        readonly app: App,
        protected timeoutService: TimeoutService,
        protected elementRef: ElementRef,
        protected changeDetectorRef: ChangeDetectorRef,
        protected readonly formWizardComponent: FormWizardComponent
    ) {}
    ngOnInit(): void {
        if (!this.id || !this.order) {
            this.app.logger.error(`Form wizard step requires both id [${this.id}] and order [${this.order}] attributes`);
        }
        this.formWizardComponent.registerStep(this);
    }
    ngOnDestroy(): void {
        this.formWizardComponent.unregisterStep(this);
    }
    get domElement(): HTMLElement {
        return this.elementRef.nativeElement;
    }
    get valid(): boolean {
        return this.isValid();
    }
    addForm(form: NgForm) {
        if (form && !this.forms.includes(form)) {
            this.forms.push(form);
        }
    }
    removeForm(form?: NgForm) {
        if (form) {
            const index = this.forms.indexOf(form);
            if (index > -1) {
                this.forms.splice(index, 1);
            }
        }
    }
    markSubmitted(scroll = true): void {
        this.forms.forEach(form => {
            form['submitAttempt'] = true;
            form.ngSubmit.emit();
        });
        this.submittedChange.emit(true);
        this.detectChanges();
        if (scroll && !this.valid) {
            this.timeoutService.setTimeout(() => {
                this.scrollToError();
            }, 100);
        }
    }
    markUnsubmitted(): void {
        this.forms.forEach(form => (form['submitAttempt'] = false));
        this.submittedChange.emit(false);
        this.detectChanges();
    }
    get nextStep(): NFormWizard.IStep | undefined {
        return this.formWizardComponent.getNextStep(this);
    }
    @HostBinding('class._is-current')
    get isCurrent(): boolean {
        const step = this.currentStep;
        return step ? this.id === step.id : false;
    }
    get currentStep(): NFormWizard.IStep | undefined {
        return this.formWizardComponent.currentStep;
    }
    goTo(): void {
        this.formWizardComponent.goToStep(this);
    }
    protected scrollToError(): void {
        const errorElement = find(this.domElement.querySelectorAll('.ng-invalid'), i =>
            ['input', 'select', 'textarea'].includes(i.tagName.toLowerCase())
        );
        if (errorElement) {
            const top = Math.max(0, getAbsolutePosition(errorElement as HTMLElement).y - 20);
            this.formWizardComponent.scrollToTop(top);
        }
    }
    protected getToErrorScrollOptions(): IScrollToOptions {
        return {};
    }
    protected isValid(): boolean {
        return this.areFormsValid();
    }
    protected areFormsValid(): boolean {
        return !this.forms
            .map(form => {
                form.ngSubmit.emit();
                return form.valid;
            })
            .includes(false);
    }
    protected detectChanges() {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }
}
